import React, { useState, useEffect, useRef } from 'react';
import { useDriverApp } from './DriverAppProvider';

const STATUS_CONFIG = {
  assigned:          { label: 'Assigned',             color: 'bg-gray-600',   next: 'en_route_pickup',   nextLabel: 'Start Route to Pickup' },
  en_route_pickup:   { label: 'En Route to Pickup',   color: 'bg-blue-600',   next: 'arrived_pickup',    nextLabel: 'Arrived at Pickup' },
  arrived_pickup:    { label: 'At Pickup',            color: 'bg-amber-600',  next: 'loaded',            nextLabel: 'Loaded & Departing' },
  loaded:            { label: 'Loaded',               color: 'bg-indigo-600', next: 'en_route_delivery', nextLabel: 'Start Route to Delivery' },
  en_route_delivery: { label: 'En Route to Delivery', color: 'bg-blue-600',   next: 'arrived_delivery',  nextLabel: 'Arrived at Delivery' },
  arrived_delivery:  { label: 'At Delivery',          color: 'bg-amber-600',  next: 'delivered',         nextLabel: 'Mark Delivered' },
  delivered:         { label: 'Delivered',            color: 'bg-green-600',  next: null },
  problem:           { label: 'Problem',              color: 'bg-red-600',    next: null },
};

const hapticSuccess = () => { try { navigator.vibrate?.(100); } catch {} };
const hapticError   = () => { try { navigator.vibrate?.([100, 50, 100]); } catch {} };

const fmtTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const RouteScreen = ({ load: initialLoad, onBack }) => {
  const { api, currentLocation, setActiveLoadId, user } = useDriverApp();
  const [load, setLoad] = useState(initialLoad);
  const [activeTab, setActiveTab] = useState('route');

  // Route / status tab state
  const [updating, setUpdating] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const [problemNote, setProblemNote] = useState('');
  const [updateError, setUpdateError] = useState(null);

  // POD state
  const [podStep, setPodStep] = useState(null); // null | 'preview'
  const [podFile, setPodFile] = useState(null);
  const [podPreviewUrl, setPodPreviewUrl] = useState(null);
  const [podRetryCount, setPodRetryCount] = useState(0);
  const podInputRef = useRef(null);

  // Chat tab state
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenCountRef = useRef(0);
  const messagesEndRef = useRef(null);

  // Revoke blob URL whenever it changes or component unmounts
  useEffect(() => {
    return () => { if (podPreviewUrl) URL.revokeObjectURL(podPreviewUrl); };
  }, [podPreviewUrl]);

  useEffect(() => {
    setActiveLoadId(load.id);
    return () => setActiveLoadId(null);
  }, [load.id, setActiveLoadId]);

  const refreshLoad = async () => {
    try {
      const data = await api(`/loads/${load.id}`);
      setLoad(data);
    } catch (err) {
      console.error('Failed to refresh:', err);
    }
  };

  // ── Chat ──────────────────────────────────────────────────────────────────

  const fetchMessages = async () => {
    try {
      const data = await api(`/loads/${load.id}/messages`);
      // Backend may return an array or {messages: [...]}
      const msgs = Array.isArray(data) ? data : (data.messages || []);
      setMessages(msgs);
      // Count dispatcher messages received while driver is on route tab
      if (activeTab !== 'chat') {
        const dispatcherCount = msgs.filter(m => m.sender_type === 'dispatcher').length;
        const newUnread = Math.max(0, dispatcherCount - lastSeenCountRef.current);
        setUnreadCount(newUnread);
      }
    } catch (err) {
      console.error('fetchMessages:', err);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [load.id]);

  // Scroll to bottom when new messages arrive and chat is open
  useEffect(() => {
    if (activeTab === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  // Mark messages as seen when switching to chat tab
  useEffect(() => {
    if (activeTab === 'chat') {
      const dispatcherCount = messages.filter(m => m.sender_type === 'dispatcher').length;
      lastSeenCountRef.current = dispatcherCount;
      setUnreadCount(0);
    }
  }, [activeTab]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sendingMessage) return;
    setSendingMessage(true);
    try {
      await api(`/loads/${load.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: newMessage.trim(), sender_type: 'driver' }),
      });
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // ── Status updates ────────────────────────────────────────────────────────

  const updateStatus = async (newStatus, note = '') => {
    setUpdating(true);
    try {
      await api(`/loads/${load.id}/status`, {
        method: 'POST',
        body: JSON.stringify({
          status: newStatus,
          note,
          latitude: currentLocation?.lat,
          longitude: currentLocation?.lng,
        }),
      });
      hapticSuccess();
      setUpdateError(null);
      setPodStep(null);
      setPodFile(null);
      setPodPreviewUrl(null);
      await refreshLoad();
      setShowProblem(false);
      setProblemNote('');
    } catch (err) {
      hapticError();
      setUpdateError(err.message);
      if (newStatus === 'delivered') {
        setPodRetryCount(c => c + 1);
        setPodStep(null);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (podFile) {
      try {
        const formData = new FormData();
        formData.append('photo', podFile);
        const token = localStorage.getItem('driver_app_token');
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/driver-mobile/loads/${load.id}/pod`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
        );
      } catch {
        // non-fatal — status update proceeds regardless
      }
    }
    await updateStatus('delivered');
  };

  const startPodCapture = () => {
    setUpdateError(null);
    podInputRef.current?.click();
  };

  const handlePodFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setPodFile(file);
    setPodPreviewUrl(URL.createObjectURL(file));
    setPodStep('preview');
  };

  const handlePodRetake = () => {
    setUpdateError(null);
    setPodFile(null);
    setPodPreviewUrl(null);
    setPodStep(null);
    setTimeout(() => podInputRef.current?.click(), 50);
  };

  const status = STATUS_CONFIG[load.status] || STATUS_CONFIG.assigned;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const driverName = user?.full_name || user?.name || 'Driver';

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900/50 to-gray-950 px-4 py-4 flex items-center gap-3">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{load.order_number || 'Load Details'}</h1>
          <span className={`${status.color} text-white text-xs px-2 py-0.5 rounded-full`}>
            {status.label}
          </span>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex bg-gray-900 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('route')}
          className={`flex-1 py-3 text-sm font-medium tracking-wide transition-colors relative ${
            activeTab === 'route' ? 'text-white' : 'text-gray-500'
          }`}
        >
          ROUTE
          {activeTab === 'route' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex-1 py-3 text-sm font-medium tracking-wide transition-colors relative flex items-center justify-center gap-1.5 ${
            activeTab === 'chat' ? 'text-white' : 'text-gray-500'
          }`}
        >
          CHAT
          {unreadCount > 0 && (
            <span className="bg-red-600 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {activeTab === 'chat' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
      </div>

      {/* ── ROUTE TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'route' && (
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* Route Summary */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 mb-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
              </div>
              <div>
                <p className="text-green-400 text-xs font-medium">PICKUP</p>
                <p className="text-white font-medium">{load.pickup_location || 'Address TBD'}</p>
                <p className="text-gray-500 text-sm">{load.pickup_city}, {load.pickup_state}</p>
                <p className="text-gray-600 text-xs mt-1">{formatDateTime(load.pickup_time_planned)}</p>
              </div>
            </div>

            <div className="ml-4 w-0.5 h-6 bg-gray-700 mb-4" />

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
              </div>
              <div>
                <p className="text-red-400 text-xs font-medium">DELIVERY</p>
                <p className="text-white font-medium">{load.delivery_location || 'Address TBD'}</p>
                <p className="text-gray-500 text-sm">{load.delivery_city}, {load.delivery_state}</p>
                <p className="text-gray-600 text-xs mt-1">{formatDateTime(load.delivery_time_planned)}</p>
              </div>
            </div>
          </div>

          {/* Load Details */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 mb-4">
            <h3 className="text-gray-400 text-xs font-medium mb-3">LOAD INFO</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {load.equipment_type && (
                <div>
                  <p className="text-gray-500">Equipment</p>
                  <p className="text-white">{load.equipment_type}</p>
                </div>
              )}
              {load.weight && (
                <div>
                  <p className="text-gray-500">Weight</p>
                  <p className="text-white">{load.weight.toLocaleString()} lbs</p>
                </div>
              )}
              {load.commodity && (
                <div className="col-span-2">
                  <p className="text-gray-500">Commodity</p>
                  <p className="text-white">{load.commodity}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Update */}
          {load.status === 'arrived_delivery' ? (
            <div className="mb-3">
              <input
                ref={podInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePodFileSelect}
                className="hidden"
              />

              {podStep === 'preview' ? (
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <img
                    src={podPreviewUrl}
                    alt="Proof of delivery"
                    className="w-full max-h-64 object-cover"
                  />
                  <div className="p-3 space-y-2">
                    <button
                      onClick={handleConfirmDelivery}
                      disabled={updating}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl disabled:opacity-50"
                    >
                      {updating ? 'Confirming...' : 'CONFIRM DELIVERY'}
                    </button>
                    <button
                      onClick={handlePodRetake}
                      disabled={updating}
                      className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 rounded-xl disabled:opacity-50"
                    >
                      RETAKE
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {updateError && (
                    <div className="bg-red-900/30 border border-red-600/50 rounded-xl p-3 mb-3">
                      <p className="text-red-400 text-sm">
                        {updateError}
                        {podRetryCount > 0 && ' Please contact dispatch if this continues.'}
                      </p>
                    </div>
                  )}
                  <button
                    onClick={startPodCapture}
                    disabled={updating}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    TAKE POD PHOTO
                  </button>
                </>
              )}
            </div>
          ) : (
            status.next && (
              <>
                {updateError && (
                  <div className="bg-red-900/30 border border-red-600/50 rounded-xl p-3 mb-3">
                    <p className="text-red-400 text-sm">{updateError}</p>
                  </div>
                )}
                <button
                  onClick={() => updateStatus(status.next)}
                  disabled={updating}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl mb-3 disabled:opacity-50"
                >
                  {updating ? 'Updating...' : status.nextLabel}
                </button>
              </>
            )
          )}

          {/* Problem Report */}
          {load.status !== 'delivered' && load.status !== 'problem' && (
            <>
              {!showProblem ? (
                <button
                  onClick={() => setShowProblem(true)}
                  className="w-full bg-red-600/20 border border-red-600/50 text-red-400 py-3 rounded-xl"
                >
                  Report Problem
                </button>
              ) : (
                <div className="bg-gray-900 rounded-xl border border-red-600/50 p-4">
                  <textarea
                    value={problemNote}
                    onChange={(e) => setProblemNote(e.target.value)}
                    placeholder="Describe the problem..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg py-3 px-4 text-white placeholder-gray-500 mb-3 resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowProblem(false)}
                      className="flex-1 bg-gray-700 text-white py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateStatus('problem', problemNote)}
                      disabled={!problemNote.trim() || updating}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg disabled:opacity-50"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── CHAT TAB ───────────────────────────────────────────────────── */}
      {activeTab === 'chat' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {chatLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-gray-400 font-medium">No messages yet</p>
                <p className="text-gray-600 text-sm mt-1">Dispatch messages will appear here</p>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isDriver = msg.sender_type === 'driver';
                const ts = msg.sent_at || msg.created_at;
                return (
                  <div key={msg.id || idx} className={`flex ${isDriver ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex flex-col gap-0.5 max-w-[80%] ${isDriver ? 'items-end' : 'items-start'}`}>
                      {!isDriver && (
                        <span className="text-xs text-gray-500 ml-1">
                          {msg.sender_name || 'Dispatch'}
                        </span>
                      )}
                      <div className={`px-4 py-2 text-sm leading-relaxed rounded-2xl ${
                        isDriver
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-800 text-white rounded-bl-sm'
                      }`}>
                        {msg.content}
                      </div>
                      <span className="text-[11px] text-gray-600 mx-1">{fmtTime(ts)}</span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 bg-gray-900 border-t border-gray-800 flex gap-2 items-center">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              placeholder="Message dispatch…"
              disabled={sendingMessage}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-full py-3 px-4 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center disabled:opacity-50 flex-shrink-0"
            >
              {sendingMessage
                ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteScreen;
